/**
 * "Who will approve this, and why them": the chain a document would follow,
 * asked of the same resolver the submission uses. Shown to an author before
 * they submit, and to an administrator asking "what would happen if".
 *
 * It never blocks: if the chain cannot be shown, submitting still works and
 * the server chooses. It does say when a step is held by a role nobody has,
 * because that is the moment to find out, not two weeks later.
 */

import { ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { useRoutePreview } from './approvalsData';
import { Spinner } from '../../app/Spinner';
import own from './Approvals.module.css';

type Ask = { policyId: string; workflowId?: string } | { documentTypeId: string | null; departmentId: string | null };

export function holdersText(holders: number | null): string | null {
  if (holders === null) return null;
  if (holders === 0) return 'Nobody holds this role. Only an administrator can decide this step.';
  return holders === 1 ? '1 person will be asked' : `${holders} people will be asked`;
}

export function RoutePreview({ ask, heading, showChoices = false }: { ask: Ask | null; heading: string; showChoices?: boolean }) {
  const preview = useRoutePreview(ask);
  if (ask === null) return null;

  return (
    <section className={own.route} aria-label={heading}>
      <h4 className={own.routeTitle}>{heading}</h4>
      {preview.isLoading ? (
        <Spinner label="Working out the approval chain" />
      ) : preview.error || !preview.data || !Array.isArray(preview.data.steps) ? (
        <p className={own.itemMeta}>
          The approval chain could not be shown just now. Submitting still works: the chain is chosen when you submit.
        </p>
      ) : (
        <>
          <ol className={own.exampleSteps}>
            {preview.data.steps.map((step) => (
              <li key={`${step.order}-${step.name}`}>
                <span className={own.exampleOrder} aria-hidden="true">{step.order}</span>
                <span>
                  <span className="vh">Step {step.order}: </span>
                  {step.name}
                  <span className={own.exampleRole}>
                    {' · '}{step.roleId && isRole(step.roleId) ? ROLE_DISPLAY_NAMES[step.roleId] : 'No role assigned'}
                  </span>
                  {(step.named || step.departmentOnly || step.sameTimeAsPrevious) && (
                    <span className={own.exampleRole}>
                      {step.named ? ' · named people' : ''}{step.departmentOnly ? " · this document's department only" : ''}{step.sameTimeAsPrevious ? ' · at the same time as the step above' : ''}
                    </span>
                  )}
                  {step.holders === 0 && (
                    <span className={own.routeWarn} role="note"> {holdersText(0)}</span>
                  )}
                  {step.holders !== null && step.holders > 0 && (
                    <span className={own.exampleRole}> · {holdersText(step.holders)}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
          {preview.data.route ? (
            <p className={own.itemMeta}>
              Workflow: <strong>{preview.data.route.workflowName}</strong>. {preview.data.route.reason}
              {preview.data.route.skipped.map((s) => (
                <span key={s.workflowId}> &ldquo;{s.workflowName}&rdquo; would have applied but has no steps, so it was passed over.</span>
              ))}
            </p>
          ) : (
            <p className={own.itemMeta}>
              No approval workflow is set up yet, so a single Approval step is created when this is submitted.
            </p>
          )}
          {showChoices && (preview.data.choices ?? []).length > 0 && (
            <p className={own.itemMeta}>
              {preview.data.route?.level === 'catch_all'
                ? 'That is the default. Because it is the catch-all, an author may choose instead, and say why:'
                : 'That is the default, and an author cannot replace it. An administrator overriding it may choose:'}{' '}
              {(preview.data.choices ?? []).map((c) => c.name).join(', ')}.
            </p>
          )}
        </>
      )}
    </section>
  );
}
